import { useCallback, useEffect, useRef } from "react"

import { useErrorFallbackShown } from "@/lib/appErrorFallback"
import {
  COVI_OPEN_HOME_EVENT,
  COVI_OPEN_WITH_PROMPT_EVENT,
  COVI_OPEN_WITH_SUMMARY_EVENT,
  type CoviOpenWithPromptDetail,
  type CoviOpenWithSummaryDetail,
} from "@/lib/coviEvents"

import type { OpenThreadOptions } from "../contexts/CoviContext.types"
import { nextChatItemId } from "../utils/chatTurnPayload"
import { markSummaryItemForReveal } from "../utils/summaryReveal"

interface CoviOpenRequestDeps {
  open: (via?: "prompt" | "auto_open") => void
  openHome: () => void
  openThread: (opts?: OpenThreadOptions) => string
  showThreadOnHome: (threadId: string) => void
  /** Threads that must survive a prune: streaming, drafted, or on screen. */
  residentThreadIds: ReadonlySet<string>
}

/**
 * Bridges the window-event channel other modules use to open Sofia.
 *
 * "Discuss with Sofia"-style entry points live in product modules that cannot
 * import app-covi (it already depends on them, so the import would close a
 * module-graph cycle). They dispatch events instead; this hook owns the
 * listeners and the two ways a page can open Sofia with content attached. See
 * `@/lib/coviEvents`.
 *
 * While an error boundary fallback is on screen the channel stands down with
 * the rest of Sofia: see the handlers below.
 */
export function useCoviOpenRequests({
  open,
  openHome,
  openThread,
  showThreadOnHome,
  residentThreadIds,
}: CoviOpenRequestDeps): {
  openWithPrompt: (prompt: string, surface?: "panel" | "home") => void
  openWithSummary: (title: string, markdown: string) => void
} {
  // CoviMount hides every Sofia surface while a boundary fallback has replaced
  // the page, but the provider stays mounted above that boundary and its
  // listeners with it. Gating them here keeps the two in step, so a late or
  // out-of-tree dispatch cannot seed a thread or record the crashed page as the
  // Home return path behind the error screen.
  const errorFallbackShown = useErrorFallbackShown()

  // Opens a NEW thread seeded with the prompt, so a page-triggered ask is never
  // appended to the user's conversation. Whether that new thread is allowed to
  // become the one on screen is the presenter's call, not this one's.
  const openWithPrompt = useCallback(
    (prompt: string, surface: "panel" | "home" = "panel") => {
      const trimmed = prompt.trim()
      let threadId: string | null = null
      if (trimmed) {
        threadId = openThread({
          seedPrompt: trimmed,
          // "home" is the user typing into the Home landing composer — their own
          // new chat, and now the only way to start one, so it prunes like any
          // user action. "panel" is a page handing Sofia a summary prompt.
          preserveExistingThreads: surface !== "home",
          // The Home composer prunes, so it needs the same residency guard the
          // sidebar's New chat uses: only the active thread is protected by
          // default, which leaves a thread streaming behind the briefing free to
          // be evicted — and eviction deletes its slot, unmounting the turn.
          protectedThreadIds: residentThreadIds,
        })
      }
      if (surface === "home" && threadId) {
        // Home routes to the full-page surface; CoviMount opens the runtime from
        // the URL so the docked panel never flashes mid-transition. See
        // showThreadOnHome.
        showThreadOnHome(threadId)
      } else {
        open("prompt")
      }
    },
    [open, openThread, residentThreadIds, showThreadOnHome],
  )

  // A page's Sofia summary is already final prose, so the thread is seeded with
  // the text itself rather than asking the model to restate it: no turn spent,
  // no wording drift. It is an ordinary assistant message from there on, so
  // follow-ups are answered against exactly what is on screen.
  //
  // The seeded item is registered for reveal so it plays in — think, then type
  // — instead of sitting there the moment the panel opens. See
  // `useSofiaSummaryReveal`.
  const openWithSummary = useCallback(
    (title: string, markdown: string) => {
      const trimmed = markdown.trim()
      if (!trimmed) {
        return
      }
      const itemId = nextChatItemId("a")
      markSummaryItemForReveal(itemId)
      openThread({
        title,
        seedItems: [{ kind: "assistant", id: itemId, text: trimmed }],
        // Page-triggered, so it must never prune a conversation the user
        // started — same rationale as openWithPrompt's panel surface. Note this
        // protects threads from EVICTION only; it does not stop the new thread
        // becoming active. `useSofiaPageSummaryPresenter` is what keeps a
        // summary from taking the surface off an engaged thread.
        preserveExistingThreads: true,
        // On trial: the user did not ask for this chat, so it earns its place
        // by being answered. Browsing pages leaves no chats behind.
        ephemeral: true,
      })
      open("auto_open")
    },
    [open, openThread],
  )

  // The listeners are registered once and read the current handlers through a
  // ref. Keying the registration on handler identity instead opens a drop
  // window: a dispatch can originate from another component's effect in the
  // very commit that rebuilt these handlers (the page-summary presenter opens
  // on the panel-open commit), and React runs that effect after this one's
  // cleanup removed the old listeners but before it re-added new ones — the
  // event then vanishes without a trace.
  const handlersRef = useRef({
    errorFallbackShown,
    openHome,
    openWithPrompt,
    openWithSummary,
  })
  useEffect(() => {
    handlersRef.current = {
      errorFallbackShown,
      openHome,
      openWithPrompt,
      openWithSummary,
    }
  })

  useEffect(() => {
    const handleOpenWithPrompt = (event: Event) => {
      if (handlersRef.current.errorFallbackShown) {
        return
      }
      const detail = (event as CustomEvent<CoviOpenWithPromptDetail>).detail
      if (detail?.prompt) {
        handlersRef.current.openWithPrompt(detail.prompt, detail.surface)
      }
    }
    const handleOpenWithSummary = (event: Event) => {
      if (handlersRef.current.errorFallbackShown) {
        return
      }
      const detail = (event as CustomEvent<CoviOpenWithSummaryDetail>).detail
      if (detail?.markdown) {
        handlersRef.current.openWithSummary(detail.title, detail.markdown)
      }
    }
    // Acknowledge the request so the dispatcher knows Sofia is mounted and can
    // suppress whatever fallback navigation it would otherwise allow.
    const handleOpenHome = (event: Event) => {
      // Leaving the event uncancelled is the point: the caller reads that as
      // "no Sofia here" and performs its own plain /home navigation, which is
      // what the user asked for and what clears the fallback. Answering instead
      // would route through showDailyBriefing, which files the crashed page as
      // homeReturnPath and sends the user back into it on close.
      if (handlersRef.current.errorFallbackShown) {
        return
      }
      event.preventDefault()
      handlersRef.current.openHome()
    }
    window.addEventListener(COVI_OPEN_WITH_PROMPT_EVENT, handleOpenWithPrompt)
    window.addEventListener(COVI_OPEN_WITH_SUMMARY_EVENT, handleOpenWithSummary)
    window.addEventListener(COVI_OPEN_HOME_EVENT, handleOpenHome)
    return () => {
      window.removeEventListener(
        COVI_OPEN_WITH_PROMPT_EVENT,
        handleOpenWithPrompt,
      )
      window.removeEventListener(
        COVI_OPEN_WITH_SUMMARY_EVENT,
        handleOpenWithSummary,
      )
      window.removeEventListener(COVI_OPEN_HOME_EVENT, handleOpenHome)
    }
  }, [])

  return { openWithPrompt, openWithSummary }
}
